import React from "react";
import { AlertCircle, Check } from "lucide-react";
import ProgressBar from "./ProgressBar";
import cn from "./cn";

/**
 * StepRail — a guided flow's steps: where the person is, what is done, what
 * comes next. The assessment builder, the question editor and the bulk upload
 * all walk their steps through it.
 *
 *   steps    [{ key, title, hint? }]
 *   current  index of the step on screen
 *   reached  the furthest index visited (every step up to it is done)
 *   onSelect (i) => void — omit it and the steps are a read-out, not buttons:
 *            the bulk upload moves on when a file is read, not when a step is
 *            clicked
 *   flagged  keys of steps behind the person that still need something
 *   variant  'rail' (default) — a vertical list beside the form on a desktop
 *            'bar'            — a horizontal row above it
 *
 * An ordered list, the current step `aria-current="step"`. A step is reachable
 * once it has been visited, so the order is a guide that can be seen rather
 * than a rule discovered by bumping into it. Below 992px both variants collapse
 * to "Step 3 of 6 · Questions" over a progress bar.
 */
export default function StepRail({ steps, current, reached, onSelect, flagged = [], variant = "rail" }) {
  const pct = steps.length > 1 ? Math.round((current / (steps.length - 1)) * 100) : 100;
  const bar = variant === "bar";

  return (
    <>
      <div className="tw-flex tw-flex-col tw-gap-2 min-[992px]:tw-hidden">
        <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
          Step {current + 1} of {steps.length}
          <span className="tw-font-semibold tw-text-content"> · {steps[current]?.title}</span>
        </p>
        <ProgressBar value={pct} color="primary" />
      </div>

      <nav aria-label="Steps" className="tw-hidden min-[992px]:tw-block">
        <ol
          className={cn(
            "tw-m-0 tw-flex tw-list-none tw-p-0",
            bar ? "tw-flex-row tw-items-center tw-gap-2" : "tw-flex-col tw-gap-1",
          )}
        >
          {steps.map((s, i) => {
            const isCurrent = i === current;
            const done = !isCurrent && i <= reached;
            const enabled = i <= reached;
            const warn = flagged.includes(s.key) && !isCurrent;
            const Tag = onSelect ? "button" : "span";
            return (
              <li key={s.key} className={cn(bar && "tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-2")}>
                <Tag
                  {...(onSelect
                    ? { type: "button", onClick: () => onSelect(i), disabled: !enabled }
                    : {})}
                  aria-current={isCurrent ? "step" : undefined}
                  className={cn(
                    "tw-flex tw-items-start tw-gap-3 tw-rounded-md tw-border-0 tw-px-3 tw-py-2.5 tw-text-left",
                    !bar && "tw-w-full",
                    bar && "tw-min-w-0 tw-shrink-0 tw-items-center",
                    onSelect && "tw-focus-ring",
                    isCurrent ? "tw-bg-primary-soft" : "tw-bg-transparent",
                    onSelect && !isCurrent && "hover:tw-bg-surface-hover",
                    onSelect && (enabled ? "tw-cursor-pointer" : "tw-cursor-not-allowed tw-opacity-55"),
                    !onSelect && !enabled && "tw-opacity-55",
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      "tw-mt-px tw-flex tw-h-6 tw-w-6 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-text-xs tw-font-semibold",
                      isCurrent && "tw-border-primary-solid tw-bg-primary-solid tw-text-primary-solid-fg",
                      !isCurrent && done && "tw-border-primary tw-bg-surface-raised tw-text-primary",
                      !isCurrent && !done && "tw-border-line tw-bg-surface-raised tw-text-content-muted",
                    )}
                  >
                    {done ? <Check size={13} strokeWidth={3} /> : i + 1}
                  </span>
                  <span className="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
                    <span className={cn("tw-text-sm tw-leading-tight tw-text-content", isCurrent ? "tw-font-semibold" : "tw-font-medium")}>
                      {s.title}
                      {done && <span className="tw-sr-only"> (done)</span>}
                      {warn && (
                        <>
                          <AlertCircle size={13} aria-hidden="true" className="tw-ml-1.5 tw-inline tw-align-[-2px] tw-text-warning" />
                          <span className="tw-sr-only"> (needs attention)</span>
                        </>
                      )}
                    </span>
                    {s.hint && !bar && <span className="tw-text-xs tw-leading-snug tw-text-content-subtle">{s.hint}</span>}
                  </span>
                </Tag>
                {bar && i < steps.length - 1 && (
                  <span
                    aria-hidden="true"
                    className={cn("tw-h-px tw-min-w-4 tw-flex-1", i < reached ? "tw-bg-primary" : "tw-bg-line")}
                  />
                )}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
}
